// =============================================================================
// Runtime API URL Detection
// =============================================================================
// Supports unified deployment where a single build serves all instances.
// API URL is determined at runtime based on the hostname.
//
// Patterns:
//   view1.ospitek.com     -> api.view1.ospitek.com
//   view2.ospitek.com     -> api.view2.ospitek.com
//   testing.ospitek.com   -> api.testing.ospitek.com
//   localhost             -> http://localhost:8080
//
// Build-time override: Set VITE_API_URL to override runtime detection.
// =============================================================================

/**
 * Get API URL based on current hostname.
 * Supports unified deployment across all instances.
 */
const getApiUrl = () => {
  // Allow build-time override for special deployments or local development
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }

  // Production pattern: {instance}.ospitek.com -> api.{instance}.ospitek.com
  // Handles: view1.ospitek.com, view2.ospitek.com, testing.ospitek.com, etc.
  if (hostname.endsWith('.ospitek.com')) {
    const subdomain = hostname.replace('.ospitek.com', '');
    return `https://api.${subdomain}.ospitek.com`;
  }

  // Legacy or unknown hostname - log warning and use localhost fallback
  console.warn(`[config] Unknown hostname: ${hostname}, using localhost API`);
  return 'http://localhost:8080';
};

/**
 * Get Care URL (patient portal) based on current hostname.
 */
const getCareUrl = () => {
  // Allow build-time override
  if (import.meta.env.VITE_CARE_URL) {
    return import.meta.env.VITE_CARE_URL;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:4000';
  }

  // Production pattern: {instance}.ospitek.com -> care.{instance}.ospitek.com
  if (hostname.endsWith('.ospitek.com')) {
    const subdomain = hostname.replace('.ospitek.com', '');
    return `https://care.${subdomain}.ospitek.com`;
  }

  return 'http://localhost:4000';
};

/**
 * Get App URL (current application) based on current hostname.
 */
const getAppUrl = () => {
  // Allow build-time override
  if (import.meta.env.VITE_URL) {
    return import.meta.env.VITE_URL;
  }

  // Use current origin for app URL
  return window.location.origin;
};

/**
 * Get Covid Screening URL based on current hostname.
 */
const getCovidScreeningUrl = () => {
  // Allow build-time override
  if (import.meta.env.VITE_CS_URL) {
    return import.meta.env.VITE_CS_URL;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }

  // Production pattern: {instance}.ospitek.com -> covid.{instance}.ospitek.com
  if (hostname.endsWith('.ospitek.com')) {
    const subdomain = hostname.replace('.ospitek.com', '');
    return `https://covid.${subdomain}.ospitek.com`;
  }

  return 'http://localhost:5000';
};

export default {
  hospitalName: 'University Ambulatory Surgery Center',
  nodeEnv: import.meta.env.MODE,
  publicURL: getAppUrl(),
  apiURL: getApiUrl(),
  appURL: getAppUrl(),
  careURL: getCareUrl(),
  covidScreeningURL: getCovidScreeningUrl(),
  assetsURL: import.meta.env.ASSETS_URL || 'https://assets.ospitek.com',
  appVersion: import.meta.env.VITE_VERSION,
};
